import { loadChargePoints, loadChargePointsByGridConnectionId } from '@/features/ChargePoints';
import log from '@/lib/logger';
import type { AppDispatch } from '@/store';
import type { NormalizedAssetResponse } from '@/types/client';
import type { Asset } from '@/types/swagger';
import type { AxiosError } from 'axios';
import { http } from 'config';
import { loadGridConnections } from 'features/GridConnections';
import { assetListSchema, assetSchema } from 'features/schemas';
import { normalize } from 'normalizr';
import Nprogress from 'nprogress';
import { callNextPage } from 'util/callNextPage';

import { assetsFetching, loadAssets } from '.';

export const fetchAsset = (assetId: number) => async (dispatch: AppDispatch) => {
  const apiAssets = `company/assets/${assetId}/`;
  Nprogress.start();
  return http
    .get(apiAssets)
    .then((response) => {
      const normalizedData: NormalizedAssetResponse = normalize(response.data, assetSchema);
      const { assets, grid_connections } = normalizedData.entities;
      if (assets) dispatch(loadAssets(assets));
      if (grid_connections) dispatch(loadGridConnections(grid_connections));
      Nprogress.done();
      return response.data;
    })
    .catch((error) => {
      Nprogress.done();
      log.error(error);
      return error;
    });
};

export const fetchAssets = () => async (dispatch: AppDispatch) => {
  const apiAssets = 'company/assets/?page_size=999';
  Nprogress.start();
  dispatch(assetsFetching('pending'));
  const resolve = (data: { results: Asset[] }) => {
    const normalizedData: NormalizedAssetResponse = normalize(data, assetListSchema);
    const { assets, grid_connections, charge_points } = normalizedData.entities;
    if (!!assets) dispatch(loadAssets(assets));
    if (!!grid_connections) dispatch(loadGridConnections(grid_connections));
    if (!!charge_points) {
      dispatch(loadChargePoints(charge_points));
      const gridConnectionIds = Object.keys(grid_connections).map(Number);
      const chargePointIds = Object.keys(charge_points).map(Number);
      gridConnectionIds.forEach((gridConnectionId) => {
        let chargePointsGroupedByGridConnection = {};
        chargePointIds.forEach((chargePointId) => {
          if (charge_points[chargePointId].grid_connection === gridConnectionId)
            chargePointsGroupedByGridConnection = {
              ...chargePointsGroupedByGridConnection,
              [chargePointId]: charge_points[chargePointId],
            };
        });
        dispatch(
          loadChargePointsByGridConnectionId(gridConnectionId, chargePointsGroupedByGridConnection)
        );
      });
    }
    Nprogress.done();
    dispatch(assetsFetching('success'));
    return data;
  };
  const reject = (error: AxiosError) => {
    Nprogress.done();
    dispatch(assetsFetching('error'));
    log.error(error);
    return error;
  };
  return callNextPage(apiAssets, resolve, reject);
};
